import { useState, useEffect, useCallback, useMemo } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

// ─── Icon helper ─────────────────────────────────────────
function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

/* ────────────────────────────────────────────
   Types
   ──────────────────────────────────────────── */

interface FacturaBorrada {
  id: string;
  estado: string;
  tipo: 'proveedor' | 'cliente';
  apunte: string;
  fecha_subida: string;
  punteado: boolean;
  num_factura: string;
  tercero: string;
  fecha_contabilizacion: string;
  importe_total: number;
  base_iva_21: number;
  iva_21: number;
  fecha_factura: string;
  fecha_vencimiento: string;
  metodo_pago: string;
  porcentaje_retencion: number;
  retencion: number;
  nombre: string;
  cliente_id: string;
  deleted_at: string | null;
  clientes?: { razon_social: string };
}

/* ────────────────────────────────────────────
   Mock data generator (fallback when table
   structure is uncertain)
   ──────────────────────────────────────────── */

function generarDatosMock(): FacturaBorrada[] {
  const nombres = [
    'Suministros García S.L.',
    'Distribuciones Norte S.A.',
    'Tech Solutions Ibérica',
    'Consultora Meridian',
    'Transportes Veloz S.L.',
    'Alimentación Selecta',
    'Grupo Financiero Delta',
    'Inmobiliaria Castilla',
  ];
  const metodos = ['Transferencia', 'Domiciliación', 'Cheque', 'Efectivo', 'Pagaré'];

  return Array.from({ length: 23 }, (_, i) => {
    const base = Math.round((Math.random() * 8000 + 200) * 100) / 100;
    const iva = Math.round(base * 0.21 * 100) / 100;
    const retPct = Math.random() > 0.7 ? 15 : 0;
    const ret = Math.round(base * (retPct / 100) * 100) / 100;
    const total = Math.round((base + iva - ret) * 100) / 100;
    const day = String(Math.floor(Math.random() * 28) + 1).padStart(2, '0');
    const month = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');

    return {
      id: `mock-${i + 1}`,
      estado: 'eliminado',
      tipo: Math.random() > 0.4 ? 'proveedor' : 'cliente',
      apunte: `${4000 + i}`,
      fecha_subida: `2025-${month}-${day}`,
      punteado: Math.random() > 0.5,
      num_factura: `FRA-${2025}-${String(i + 1).padStart(4, '0')}`,
      tercero: nombres[i % nombres.length],
      fecha_contabilizacion: `2025-${month}-${day}`,
      importe_total: total,
      base_iva_21: base,
      iva_21: iva,
      fecha_factura: `2025-${month}-${day}`,
      fecha_vencimiento: `2025-${month}-${String(Math.min(28, parseInt(day) + 30)).padStart(2, '0')}`,
      metodo_pago: metodos[Math.floor(Math.random() * metodos.length)],
      porcentaje_retencion: retPct,
      retencion: ret,
      nombre: `Factura ${i + 1}`,
      cliente_id: `cliente-${(i % 4) + 1}`,
      deleted_at: new Date().toISOString(),
      clientes: { razon_social: nombres[i % nombres.length] },
    };
  });
}

/* ────────────────────────────────────────────
   Component
   ──────────────────────────────────────────── */

export default function Papelera() {
  const { user, loading } = useAuth();
  const router = useRouter();

  // Data
  const [documentos, setDocumentos] = useState<FacturaBorrada[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [usandoMock, setUsandoMock] = useState(false);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Search
  const [busqueda, setBusqueda] = useState('');
  const [mostrarBusqueda, setMostrarBusqueda] = useState(false);

  // Sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Pagination
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);

  // Actions
  const [restaurando, setRestaurando] = useState(false);

  /* ── Load deleted documents ── */

  const cargarDocumentosBorrados = useCallback(async () => {
    try {
      setLoadingData(true);

      const { data: assignedClients } = await supabase
        .from('asesor_cliente')
        .select('cliente_id')
        .eq('asesor_id', user?.id);

      const clientIds = assignedClients?.map((ac) => ac.cliente_id) || [];

      if (clientIds.length === 0) {
        setDocumentos([]);
        setLoadingData(false);
        return;
      }

      // Try fetching documents marked as deleted
      const { data: docs, error } = await supabase
        .from('documentos')
        .select('*, clientes(*)')
        .in('cliente_id', clientIds)
        .eq('estado', 'eliminado')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching deleted docs, falling back to mock:', error);
        setDocumentos(generarDatosMock());
        setUsandoMock(true);
      } else if (!docs || docs.length === 0) {
        // Also try deleted_at not null
        const { data: docsDeleted, error: errDeleted } = await supabase
          .from('documentos')
          .select('*, clientes(*)')
          .in('cliente_id', clientIds)
          .not('deleted_at', 'is', null)
          .order('created_at', { ascending: false });

        if (errDeleted || !docsDeleted || docsDeleted.length === 0) {
          setDocumentos(generarDatosMock());
          setUsandoMock(true);
        } else {
          setDocumentos(
            docsDeleted.map((d) => mapDocToFactura(d))
          );
        }
      } else {
        setDocumentos(docs.map((d) => mapDocToFactura(d)));
      }
    } catch (error) {
      console.error('Error loading deleted documents:', error);
      toast.error('Error al cargar la papelera');
      setDocumentos(generarDatosMock());
      setUsandoMock(true);
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  /** Map raw Supabase row to FacturaBorrada */
  function mapDocToFactura(doc: Record<string, unknown>): FacturaBorrada {
    const datos = (doc.datos_ia as Record<string, unknown>) || {};
    const base = Number(datos.baseImponible) || 0;
    const iva = Number(datos.cuotaIVA) || Math.round(base * 0.21 * 100) / 100;
    const retPct = Number(datos.porcentajeRetencion) || 0;
    const ret = Math.round(base * (retPct / 100) * 100) / 100;
    const total = Number(datos.totalFactura) || Number(datos.importeTotal) || base + iva - ret;

    return {
      id: doc.id as string,
      estado: doc.estado as string,
      tipo: ((doc.tipo as string) || '').includes('recibida') ? 'proveedor' : 'cliente',
      apunte: (doc.nro_asiento_erp as string) || '',
      fecha_subida: (doc.fecha_subida as string) || (doc.created_at as string) || '',
      punteado: (doc.punteado as boolean) || false,
      num_factura: (datos.numeroFactura as string) || '',
      tercero: (datos.razonSocialEmisor as string) || (datos.proveedor as string) || '',
      fecha_contabilizacion: (doc.fecha_contabilizacion as string) || '',
      importe_total: total,
      base_iva_21: base,
      iva_21: iva,
      fecha_factura: (datos.fechaFactura as string) || (datos.fecha as string) || '',
      fecha_vencimiento: (datos.fechaVencimiento as string) || '',
      metodo_pago: (datos.metodoPago as string) || '',
      porcentaje_retencion: retPct,
      retencion: ret,
      nombre: doc.nombre as string,
      cliente_id: doc.cliente_id as string,
      deleted_at: (doc.deleted_at as string) || null,
      clientes: doc.clientes as { razon_social: string } | undefined,
    };
  }

  /* ── Auth guard ── */

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (user && user.rol === 'asesor') {
      cargarDocumentosBorrados();
    }
  }, [user, loading, router, cargarDocumentosBorrados]);

  /* ── Filtering ── */

  const documentosFiltrados = useMemo(() => {
    if (!busqueda.trim()) return documentos;
    const q = busqueda.toLowerCase();
    return documentos.filter(
      (d) =>
        d.tercero.toLowerCase().includes(q) ||
        d.num_factura.toLowerCase().includes(q) ||
        d.apunte.toLowerCase().includes(q) ||
        d.nombre.toLowerCase().includes(q) ||
        d.metodo_pago.toLowerCase().includes(q)
    );
  }, [documentos, busqueda]);

  /* ── Sorting ── */

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const documentosOrdenados = useMemo(() => {
    if (!sortColumn) return documentosFiltrados;
    return [...documentosFiltrados].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortColumn) {
        case 'estado':
          aVal = a.estado;
          bVal = b.estado;
          break;
        case 'tipo':
          aVal = a.tipo;
          bVal = b.tipo;
          break;
        case 'apunte':
          aVal = a.apunte;
          bVal = b.apunte;
          break;
        case 'fecha_subida':
          aVal = new Date(a.fecha_subida).getTime();
          bVal = new Date(b.fecha_subida).getTime();
          break;
        case 'num_factura':
          aVal = a.num_factura;
          bVal = b.num_factura;
          break;
        case 'tercero':
          aVal = a.tercero.toLowerCase();
          bVal = b.tercero.toLowerCase();
          break;
        case 'importe_total':
          aVal = a.importe_total;
          bVal = b.importe_total;
          break;
        case 'fecha_factura':
          aVal = new Date(a.fecha_factura || 0).getTime();
          bVal = new Date(b.fecha_factura || 0).getTime();
          break;
        case 'fecha_vencimiento':
          aVal = new Date(a.fecha_vencimiento || 0).getTime();
          bVal = new Date(b.fecha_vencimiento || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [documentosFiltrados, sortColumn, sortDirection]);

  /* ── Pagination ── */

  const totalPaginas = Math.ceil(documentosOrdenados.length / porPagina);
  const inicio = (pagina - 1) * porPagina;
  const fin = Math.min(inicio + porPagina, documentosOrdenados.length);
  const documentosPaginados = documentosOrdenados.slice(inicio, fin);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagina(1);
  }, [busqueda, porPagina]);

  /* ── Selection ── */

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const idsEnPagina = documentosPaginados.map((d) => d.id);
    const todosSeleccionados =
      idsEnPagina.length > 0 && idsEnPagina.every((id) => selectedIds.has(id));
    if (todosSeleccionados) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        idsEnPagina.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        idsEnPagina.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const haySeleccion = selectedIds.size > 0;

  /* ── Restore action ── */

  const handleRestaurar = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setRestaurando(true);
    const toastId = toast.loading(`Restaurando ${ids.length} documento(s)...`);

    try {
      if (usandoMock) {
        // Simulate restore with mock data
        await new Promise((resolve) => setTimeout(resolve, 800));
        setDocumentos((prev) => prev.filter((d) => !ids.includes(d.id)));
        setSelectedIds(new Set());
        toast.success(`${ids.length} documento(s) restaurado(s)`, { id: toastId });
      } else {
        let exitos = 0;
        let fallos = 0;

        for (const id of ids) {
          try {
            const { error } = await supabase
              .from('documentos')
              .update({ estado: 'pendiente', deleted_at: null })
              .eq('id', id);

            if (error) throw error;
            exitos++;
          } catch {
            fallos++;
          }
        }

        if (fallos > 0) {
          toast.error(`Restaurados: ${exitos}. Fallos: ${fallos}`, { id: toastId });
        } else {
          toast.success(`${exitos} documento(s) restaurado(s)`, { id: toastId });
        }

        setSelectedIds(new Set());
        await cargarDocumentosBorrados();
      }
    } catch (error) {
      console.error('Error restoring documents:', error);
      toast.error('Error al restaurar documentos', { id: toastId });
    } finally {
      setRestaurando(false);
    }
  };

  /* ── Export to Excel (CSV) ── */

  const handleExportExcel = () => {
    const rows = documentosOrdenados;
    if (rows.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const headers = [
      'Estado',
      'Tipo',
      'Apunte',
      'Fecha de subida',
      'Punteado',
      'Num. fra',
      'Tercero',
      'F. Contabilización',
      'Importe total',
      'Base IVA 21%',
      'IVA 21%',
      'Fecha factura',
      'Fecha vencimiento',
      'Método de pago',
      '% Ret.',
      'Retención',
    ];

    const csvRows = rows.map((d) =>
      [
        d.estado,
        d.tipo === 'proveedor' ? 'Proveedor' : 'Cliente',
        d.apunte,
        formatFecha(d.fecha_subida),
        d.punteado ? 'Sí' : 'No',
        d.num_factura,
        d.tercero,
        formatFecha(d.fecha_contabilizacion),
        d.importe_total.toFixed(2),
        d.base_iva_21.toFixed(2),
        d.iva_21.toFixed(2),
        formatFecha(d.fecha_factura),
        formatFecha(d.fecha_vencimiento),
        d.metodo_pago,
        d.porcentaje_retencion.toString(),
        d.retencion.toFixed(2),
      ]
        .map((v) => `"${v}"`)
        .join(';')
    );

    const bom = '\uFEFF';
    const csv = bom + [headers.join(';'), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `papelera-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exportación descargada');
  };

  /* ── Helpers ── */

  function formatFecha(fecha: string): string {
    if (!fecha) return '—';
    try {
      return new Date(fecha).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return fecha;
    }
  }

  function formatImporte(valor: number): string {
    return valor.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const SortIndicator = ({ column }: { column: string }) => {
    if (sortColumn !== column) return null;
    return (
      <span className="ml-1 text-calm-text">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  /* ── Loading state ── */

  if (loading || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-calm-bg">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-40 rounded-huge" />
        </div>
      </div>
    );
  }

  /* ── Column definitions for the table header ── */

  const columnas: { key: string; label: string; sticky?: boolean; align?: 'right' | 'center' }[] =
    [
      { key: 'estado', label: 'Estado' },
      { key: 'tipo', label: 'Tipo' },
      { key: 'id', label: 'Id' },
      { key: 'apunte', label: 'Apunte' },
      { key: 'fecha_subida', label: 'Fecha de subida' },
      { key: 'punteado', label: 'Punteado', align: 'center' },
      { key: 'num_factura', label: 'Num. fra' },
      { key: 'tercero', label: 'Tercero' },
      { key: 'fecha_contabilizacion', label: 'F. Contabilización' },
      { key: 'importe_total', label: 'Importe total', align: 'right' },
      { key: 'base_iva_21', label: 'Base IVA 21%', align: 'right' },
      { key: 'iva_21', label: 'IVA 21%', align: 'right' },
      { key: 'fecha_factura', label: 'Fecha factura' },
      { key: 'fecha_vencimiento', label: 'Fecha vencimiento' },
      { key: 'metodo_pago', label: 'Método de pago' },
      { key: 'porcentaje_retencion', label: '% Ret.', align: 'right' },
      { key: 'retencion', label: 'Retención', align: 'right' },
    ];

  /* ── Render ── */

  return (
    <>
      <Head>
        <title>Papelera - Externaliza2</title>
      </Head>
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] flex flex-col overflow-hidden bg-calm-bg">
          {/* ─── Header ─── */}
          <div className="shrink-0 px-12 pt-12 pb-6 space-y-6">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">Papelera</p>
              <h1 className="font-outfit text-5xl font-light text-calm-text tracking-tight">
                Facturas Borradas
              </h1>
              <div className="text-sm text-calm-muted mt-2 font-light flex items-center flex-wrap gap-2">
                {documentosFiltrados.length} documento(s) en la papelera
                {usandoMock && (
                  <Badge variant="outline" className="ml-2 text-amber-600 border-amber-200 bg-amber-50 rounded-full text-[10px]">
                    Datos de demostración
                  </Badge>
                )}
              </div>
            </div>

            {/* ─── Action bar ─── */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Refresh */}
              <button
                onClick={() => {
                  cargarDocumentosBorrados();
                  toast.success('Papelera actualizada');
                }}
                title="Actualizar"
                className="border border-calm-border rounded-huge w-9 h-9 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all"
              >
                <Icon name="sync" className="text-lg" />
              </button>

              {/* Export */}
              <button
                onClick={handleExportExcel}
                title="Exportar a Excel"
                className="border border-calm-border rounded-huge w-9 h-9 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all"
              >
                <Icon name="download" className="text-lg" />
              </button>

              {/* Search toggle */}
              <button
                onClick={() => setMostrarBusqueda(!mostrarBusqueda)}
                title="Buscar"
                className={cn(
                  'border rounded-huge w-9 h-9 flex items-center justify-center transition-all',
                  mostrarBusqueda
                    ? 'border-calm-text bg-calm-bg text-calm-text'
                    : 'border-calm-border text-calm-muted hover:bg-white hover:text-calm-text'
                )}
              >
                <Icon name="search" className="text-lg" />
              </button>

              <div className="w-px h-6 bg-calm-border mx-1" />

              {/* ACCIONES dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    disabled={!haySeleccion}
                    className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:shadow-sm transition-all disabled:opacity-40 flex items-center gap-1.5"
                  >
                    Acciones
                    <Icon name="expand_more" className="text-base" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem
                    onClick={handleRestaurar}
                    className="gap-2"
                  >
                    <Icon name="undo" className="text-base" />
                    Restaurar selección
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* RESTAURAR button */}
              <button
                disabled={!haySeleccion || restaurando}
                onClick={handleRestaurar}
                className="border border-calm-text rounded-huge px-5 py-2.5 text-sm font-light bg-calm-text text-white hover:bg-slate-800 transition-all disabled:opacity-40 flex items-center gap-1.5"
              >
                <Icon name="undo" className="text-base" />
                Restaurar
                {haySeleccion && (
                  <span className="bg-white/20 text-[11px] px-1.5 py-0 rounded-full font-medium">
                    {selectedIds.size}
                  </span>
                )}
              </button>

              {/* Search input (expandable) */}
              {mostrarBusqueda && (
                <div className="flex items-center gap-2 ml-auto">
                  <div className="relative">
                    <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-calm-accent text-[18px]" />
                    <Input
                      type="text"
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                      placeholder="Buscar por tercero, factura, apunte…"
                      className="pl-10 pr-8 w-72 border-calm-border rounded-huge text-sm font-light"
                      autoFocus
                    />
                    {busqueda && (
                      <button
                        onClick={() => setBusqueda('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-calm-accent hover:text-calm-text transition-colors"
                      >
                        <Icon name="close" className="text-base" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ─── Table ─── */}
          {documentosFiltrados.length === 0 ? (
            /* Empty state */
            <div className="flex-1 flex items-center justify-center px-12">
              <div className="border-2 border-dashed border-fresh-sky-200/60 rounded-huge bg-gradient-to-br from-white to-fresh-sky-50/20 p-16 text-center w-full max-w-lg">
                <div className="w-16 h-16 rounded-2xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-400 mb-4 mx-auto">
                  <Icon name="delete" className="text-[40px]" />
                </div>
                <p className="text-sm font-light text-calm-text mt-4">
                  No hay documentos en la papelera
                </p>
                <p className="text-[11px] text-calm-accent mt-1 font-light">
                  Los documentos eliminados aparecerán aquí
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-h-0 px-12 pb-6">
              {/* Selection indicator */}
              {haySeleccion && (
                <div className="shrink-0 mb-3 bg-calm-bg border border-calm-border rounded-huge px-4 py-3 flex items-center gap-3">
                  <span className="text-sm font-light text-calm-text">
                    {selectedIds.size} documento(s) seleccionado(s)
                  </span>
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="ml-auto text-[11px] text-calm-accent hover:text-calm-text transition-colors font-light"
                  >
                    Deseleccionar todo
                  </button>
                </div>
              )}

              {/* Scrollable table */}
              <div className="flex-1 overflow-auto bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge">
                <Table>
                  <TableHeader className="sticky top-0 z-10">
                    <TableRow className="border-b border-calm-border hover:bg-transparent">
                      {/* Checkbox column */}
                      <TableHead className="sticky left-0 z-20 bg-white w-10 p-4">
                        <Checkbox
                          checked={
                            documentosPaginados.length > 0 &&
                            documentosPaginados.every((d) => selectedIds.has(d.id))
                          }
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      {columnas.map((col) => (
                        <TableHead
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          className={cn(
                            'p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal whitespace-nowrap cursor-pointer select-none hover:text-calm-text transition-colors',
                            col.align === 'right' && 'text-right',
                            col.align === 'center' && 'text-center'
                          )}
                        >
                          {col.label}
                          <SortIndicator column={col.key} />
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documentosPaginados.map((doc) => {
                      const isSelected = selectedIds.has(doc.id);
                      return (
                        <TableRow
                          key={doc.id}
                          onClick={() => toggleSelection(doc.id)}
                          className={cn(
                            'cursor-pointer transition-colors border-b border-calm-border/50 last:border-b-0',
                            isSelected
                              ? 'bg-calm-bg hover:bg-slate-100/50'
                              : 'hover:bg-slate-50/50'
                          )}
                        >
                          {/* Checkbox */}
                          <TableCell
                            className="sticky left-0 z-10 p-4"
                            style={{
                              backgroundColor: isSelected ? 'rgb(248 250 252)' : 'white',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelection(doc.id)}
                            />
                          </TableCell>

                          {/* Estado */}
                          <TableCell className="p-4 whitespace-nowrap">
                            <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200 rounded-full">
                              Eliminado
                            </Badge>
                          </TableCell>

                          {/* Tipo */}
                          <TableCell className="p-4 whitespace-nowrap">
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px] rounded-full',
                                doc.tipo === 'proveedor'
                                  ? 'bg-orange-50 text-orange-700 border-orange-200'
                                  : 'bg-sky-50 text-sky-700 border-sky-200'
                              )}
                            >
                              {doc.tipo === 'proveedor' ? 'Proveedor' : 'Cliente'}
                            </Badge>
                          </TableCell>

                          {/* Id */}
                          <TableCell className="p-4 whitespace-nowrap text-calm-accent font-mono text-[11px] font-light">
                            {doc.id.slice(0, 8)}
                          </TableCell>

                          {/* Apunte */}
                          <TableCell className="p-4 whitespace-nowrap text-calm-text font-light text-sm">
                            {doc.apunte || '—'}
                          </TableCell>

                          {/* Fecha de subida */}
                          <TableCell className="p-4 whitespace-nowrap text-calm-muted font-light text-sm tabular-nums">
                            {formatFecha(doc.fecha_subida)}
                          </TableCell>

                          {/* Punteado */}
                          <TableCell className="p-4 text-center">
                            <span
                              className={cn(
                                'inline-block w-2.5 h-2.5 rounded-full',
                                doc.punteado ? 'bg-emerald-500' : 'bg-slate-200'
                              )}
                            />
                          </TableCell>

                          {/* Num. fra */}
                          <TableCell className="p-4 whitespace-nowrap text-calm-text font-light text-sm">
                            {doc.num_factura || '—'}
                          </TableCell>

                          {/* Tercero */}
                          <TableCell className="p-4 whitespace-nowrap text-calm-text font-light text-sm max-w-[200px] truncate">
                            {doc.tercero || '—'}
                          </TableCell>

                          {/* F. Contabilización */}
                          <TableCell className="p-4 whitespace-nowrap text-calm-muted font-light text-sm tabular-nums">
                            {formatFecha(doc.fecha_contabilizacion)}
                          </TableCell>

                          {/* Importe total */}
                          <TableCell className="p-4 whitespace-nowrap text-right text-calm-text tabular-nums text-sm font-light">
                            {formatImporte(doc.importe_total)} €
                          </TableCell>

                          {/* Base IVA 21% */}
                          <TableCell className="p-4 whitespace-nowrap text-right text-calm-muted tabular-nums text-sm font-light">
                            {formatImporte(doc.base_iva_21)} €
                          </TableCell>

                          {/* IVA 21% */}
                          <TableCell className="p-4 whitespace-nowrap text-right text-calm-muted tabular-nums text-sm font-light">
                            {formatImporte(doc.iva_21)} €
                          </TableCell>

                          {/* Fecha factura */}
                          <TableCell className="p-4 whitespace-nowrap text-calm-muted font-light text-sm tabular-nums">
                            {formatFecha(doc.fecha_factura)}
                          </TableCell>

                          {/* Fecha vencimiento */}
                          <TableCell className="p-4 whitespace-nowrap text-calm-muted font-light text-sm tabular-nums">
                            {formatFecha(doc.fecha_vencimiento)}
                          </TableCell>

                          {/* Método de pago */}
                          <TableCell className="p-4 whitespace-nowrap text-calm-muted font-light text-sm">
                            {doc.metodo_pago || '—'}
                          </TableCell>

                          {/* % Ret. */}
                          <TableCell className="p-4 whitespace-nowrap text-right text-calm-muted tabular-nums text-sm font-light">
                            {doc.porcentaje_retencion > 0
                              ? `${doc.porcentaje_retencion}%`
                              : '—'}
                          </TableCell>

                          {/* Retención */}
                          <TableCell className="p-4 whitespace-nowrap text-right text-calm-muted tabular-nums text-sm font-light">
                            {doc.retencion > 0 ? `${formatImporte(doc.retencion)} €` : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* ─── Pagination ─── */}
              <div className="shrink-0 flex items-center justify-end gap-4 pt-3 text-sm font-light text-calm-muted">
                <div className="flex items-center gap-2">
                  <span className="whitespace-nowrap">Facturas por página:</span>
                  <Select
                    value={String(porPagina)}
                    onValueChange={(val) => setPorPagina(Number(val))}
                  >
                    <SelectTrigger className="w-[70px] h-8 border-calm-border rounded-huge text-sm font-light">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <span className="tabular-nums">
                  {documentosOrdenados.length > 0
                    ? `${inicio + 1}-${fin} de ${documentosOrdenados.length}`
                    : '0 de 0'}
                </span>

                <div className="flex items-center gap-1">
                  <button
                    disabled={pagina <= 1}
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                    className="border border-calm-border rounded-huge w-8 h-8 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all disabled:opacity-40"
                  >
                    <Icon name="chevron_left" className="text-base" />
                  </button>
                  <button
                    disabled={pagina >= totalPaginas}
                    onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                    className="border border-calm-border rounded-huge w-8 h-8 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all disabled:opacity-40"
                  >
                    <Icon name="chevron_right" className="text-base" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
    </>
  );
}
